import {
    useAppInsightsContext,
    useTrackEvent,
} from '@microsoft/applicationinsights-react-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
    Anchor,
    Body1,
    Box,
    Button,
    Flex,
    Modal,
    Stack,
    notifications,
    useNavBar,
} from '@myphx-engineering/phx-design-system';
import {
    type UseFormReturnType,
    useForm,
    useHotkeys,
} from '@myphx-engineering/phx-design-system/hooks';
import { PhoneNumberFormatter } from '@phx/utils';

import { checkAccountLockoutApi } from '../../api/check-account-lockout.api';
import { loginApi } from '../../api/login.api';
import { checkTotpStatusApi } from '../../api/totp.api';
import { PageLayout } from '../../components';
import { AccountLockedOutError } from '../../components/alerts/errors/AccountLockedOut.error';
import {
    ErrorMap,
    type ErrorMessageTypes,
} from '../../components/alerts/errors/error-map';
import {
    type FormWithLillyCheckbox,
    LillyTermsCheckbox,
} from '../../components/form-fields/LillyTermsCheckbox';
import { PasswordField } from '../../components/form-fields/PasswordField';
import { PhoneNumberField } from '../../components/form-fields/PhoneNumberField';
import { SubmitButton } from '../../components/form-fields/SubmitButton';
import { CONSTANTS } from '../../constants';
import { useBrandContext } from '../../contexts/BrandProvider';
import { useLockoutCheck } from '../../hooks/use-lockout-check';
import { telemetryInstance } from '../../instrumentation';
import type { SigninFormModel } from '../../models/form.models';
import { routes } from '../../routes';
import type { MfaSelectPageState } from '../mfa/MfaSelectPage';

export type SignInPageState = {
    backToSignUp?: boolean;
    lockoutError?: boolean;
    phoneNumber?: string;
    passwordReset?: boolean;
};

const Signin = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const state = location?.state as SignInPageState;
    const appInsights = useAppInsightsContext();
    const { setBackOverrideState } = useNavBar();
    const formRef = useRef<HTMLFormElement>(null);
    const passwordResetShownRef = useRef(false);
    const { brandId } = useBrandContext();

    const [showRefreshErrorModal, setShowRefreshErrorModal] = useState(
        searchParams.has('refreshError')
    );

    const handleRefreshErrorModalClose = () => {
        setShowRefreshErrorModal(false);

        if (searchParams.has('refreshError')) {
            const url = new URL(window.location.href);
            url.searchParams.delete('refreshError');
            window.history.replaceState({}, '', url);
        }
    };

    useEffect(() => {
        if (state?.passwordReset && !passwordResetShownRef.current) {
            passwordResetShownRef.current = true;
            notifications.show({
                message: t('recoverPasswordLink.passwordUpdated'),
            });
        }
    }, [state?.passwordReset, t]);

    const trackForSubmit = useTrackEvent(
        appInsights,
        'SIGNIN_CONTINUE_CLICK',
        {}
    );
    const trackForSignup = useTrackEvent(
        appInsights,
        'SIGNIN_SIGNUP_CLICK',
        {}
    );
    const trackBackButton = useTrackEvent(appInsights, 'SIGNIN_BACK_CLICK', {});
    const trackForgotPwd = useTrackEvent(
        appInsights,
        'SIGNIN_FORGOT_PWD_CLICK',
        {}
    );
    const isLillyDirect = brandId === 'LillyDirect';

    const form = useForm<SigninFormModel>({
        initialValues: {
            phoneNumber: '',
            password: '',
            ...(isLillyDirect && { lillyTermsAccepted: false }),
        },
        validateInputOnBlur: true,
        schema: z.object({
            password: z.string(),
            phoneNumber: z.string().regex(/^\(\d{3}\) \d{3}-\d{4}$/, {
                message: t('formErrors.formErrorMessage.invalidPhoneNumber'),
            }),
            ...(isLillyDirect && { lillyTermsAccepted: z.literal(true) }),
        }),
    });

    const [loading, setLoading] = useState<boolean>(false);
    const [errorMessageType, setErrorMessageType] = useState<
        ErrorMessageTypes | undefined
    >(undefined);

    const {
        lockoutMinutes: lockoutMinutesFromPageLoad,
        showLockoutError: showLockoutErrorFromPageLoad,
    } = useLockoutCheck({
        lockoutError: state?.lockoutError,
        phoneNumber: state?.phoneNumber,
    });

    const [lockoutMinutesFromSubmit, setLockoutMinutes] = useState<
        number | undefined
    >();
    const [showLockoutErrorFromSubmit, setShowLockoutError] =
        useState<boolean>(false);

    const lockoutMinutes =
        lockoutMinutesFromSubmit ?? lockoutMinutesFromPageLoad;
    const showLockoutError =
        showLockoutErrorFromPageLoad || showLockoutErrorFromSubmit;

    const ErrorMessage = ErrorMap[errorMessageType ?? '500_GenericError'];

    const isFormValid =
        form.values.phoneNumber.length === CONSTANTS.phonePlaceHolder.length &&
        (!isLillyDirect || form.values.lillyTermsAccepted === true);

    // Read parameters passed in redirect
    const callBackUrl = searchParams.get('callback') || '/home';

    const onNavigateToSignUp = () => {
        trackForSignup({ message: 'Sign up link is clicked' });
        navigate(
            { pathname: routes.signUp, search: window.location.search },
            {
                state: { backToSignIn: true },
            }
        );
    };

    const onNavigateToForgotPassword = () => {
        trackForgotPwd({ message: 'Forgot password link is clicked' });
        navigate({
            pathname: routes.recoverSendCode,
            search: window.location.search,
        });
    };

    const onNavigateBack = useCallback(() => {
        trackBackButton({
            message: 'back button in sign-in screen is clicked',
        });
        if (state?.backToSignUp) {
            trackForSignup({
                message: 'Navigating back to sign-up screen from sign-in',
            });

            navigate(-1);
        } else {
            // go to unauth home page
            const unauthUrl = isLillyDirect
                ? import.meta.env.VITE_LILLY_UNAUTH_URL
                : import.meta.env.VITE_MYPHX_UNAUTH_URL;
            window.location.replace(unauthUrl);
        }
    }, [state, isLillyDirect, navigate, trackBackButton, trackForSignup]);

    useEffect(() => {
        setBackOverrideState({
            overrideFn: onNavigateBack,
        });

        return () => setBackOverrideState({ overrideFn: null });
    }, [onNavigateBack, setBackOverrideState]);

    const onSigninClick = async (data: SigninFormModel) => {
        trackForSubmit({ message: 'Submit button is clicked' });
        const phoneNumber = data.phoneNumber;
        const password = data.password;
        const formattedPhoneNumber =
            PhoneNumberFormatter.formatForApi(phoneNumber);

        setErrorMessageType(undefined);
        setShowLockoutError(false);
        setLoading(true);

        // Check if account is locked before attempting login
        const lockoutStatus =
            await checkAccountLockoutApi(formattedPhoneNumber);

        if (lockoutStatus.isLocked && lockoutStatus.minutesUntilUnlock) {
            setLockoutMinutes(lockoutStatus.minutesUntilUnlock);
            setShowLockoutError(true);
            setLoading(false);
            telemetryInstance.logEvent({
                name: 'SignInBlockedDueToLockout',
                source: location.pathname,
            });
            return;
        }

        // Use existing login API
        const {
            success,
            error,
            sessionId,
            kratosSessionId,
            kratosSessionToken,
        } = await loginApi(formattedPhoneNumber, password);

        if (success) {
            // Success = device token and challenge passed, can skip MFA
            const newUrl = `${callBackUrl}&session=${sessionId}&authMethod=${CONSTANTS.defaultAuth}`;
            window.location.replace(newUrl);
        } else if (error) {
            setErrorMessageType(error);
            setLoading(false);
        } else {
            // No error to show and not success in login --> OTP/TOTP flow required
            trackForSubmit({
                message: 'Login is not succeeded so redirecting to OTP flow',
            });

            try {
                // Check if user has TOTP enrolled via backend - use sessionToken for authentication
                const { error: totpCheckError } = await checkTotpStatusApi(
                    kratosSessionToken || ''
                );

                if (totpCheckError) {
                    telemetryInstance.logError({
                        source: location.pathname,
                        exception: new Error(
                            `TOTP check failed: ${totpCheckError}`
                        ),
                    });
                }

                // Navigate to MFA select page with available methods
                telemetryInstance.logEvent({
                    name: 'MFARequired',
                    source: location.pathname,
                });

                const mfaSelectState: MfaSelectPageState = {
                    phoneNumber: formattedPhoneNumber,
                    password,
                    callbackUrl: callBackUrl ?? '',
                    kratosIdentityId: kratosSessionId || '', // Keep the ID for reference
                    kratosSessionToken, // Use the token for authentication
                };

                navigate(
                    {
                        pathname: routes.mfaSelect,
                        search: window.location.search,
                    },
                    {
                        state: mfaSelectState,
                    }
                );
            } catch (apiError) {
                telemetryInstance.logError({
                    source: location.pathname,
                    exception:
                        apiError instanceof Error
                            ? apiError
                            : new Error(String(apiError)),
                });
                setErrorMessageType('500_GenericError');
                setLoading(false);
            }
        }
    };

    useHotkeys([
        [
            'Enter',
            (event: KeyboardEvent) => {
                event.preventDefault();
                event.stopPropagation();
                formRef.current?.requestSubmit();
            },
        ],
    ]);

    return (
        <PageLayout
            loadingOverlay={loading}
            title={t('signInPage.signIn')}
            footer={
                <Stack gap="sm">
                    <SubmitButton
                        form="registration-form"
                        disabled={!isFormValid}
                    >
                        {t('common.continue')}
                    </SubmitButton>
                    <Flex gap="xxxs" justify="center">
                        <Body1 ta="center" inline>
                            <Trans
                                i18nKey="signInPage.haveAccount"
                                components={{
                                    1: (
                                        <Anchor
                                            onClick={onNavigateToSignUp}
                                            data-testid="sign-up-link"
                                        />
                                    ),
                                }}
                            />
                        </Body1>
                    </Flex>
                </Stack>
            }
            header={
                <>
                    {showLockoutError && (
                        <AccountLockedOutError
                            minutesUntilUnlock={lockoutMinutes}
                        />
                    )}
                    {errorMessageType && ErrorMessage ? <ErrorMessage /> : null}
                    {brandId === 'LillyDirect' && (
                        <Flex px="sm" pt="lg">
                            <Body1>{t('signInPage.description')}</Body1>
                        </Flex>
                    )}
                </>
            }
        >
            <form
                id="registration-form"
                onSubmit={form.onSubmit(onSigninClick)}
                ref={formRef}
            >
                <Stack gap="md">
                    <PhoneNumberField
                        form={form}
                        label={t('signInPage.mobileNumber')}
                        page="SIGNIN"
                    />

                    <PasswordField
                        form={form}
                        page="SIGNIN"
                        label={t('signInPage.password')}
                        showHelpText={false}
                        enforcePasswordStrength={false}
                    />
                    <Box>
                        <Anchor
                            data-testid="navigate-forgot-password-page"
                            onClick={onNavigateToForgotPassword}
                        >
                            {t('signInPage.forgotPassword')}
                        </Anchor>
                    </Box>
                    {isLillyDirect && (
                        <LillyTermsCheckbox
                            form={
                                form as unknown as UseFormReturnType<FormWithLillyCheckbox>
                            }
                        />
                    )}
                </Stack>
            </form>
            {showRefreshErrorModal && (
                <Modal
                    opened={showRefreshErrorModal}
                    onClose={handleRefreshErrorModalClose}
                    title={t('errors.tokenRefresh.heading')}
                >
                    <Stack gap="sm">
                        <Body1>{t('errors.tokenRefresh.message')}</Body1>
                        <Button
                            fullWidth
                            onClick={handleRefreshErrorModalClose}
                        >
                            {t('errors.tokenRefresh.cta')}
                        </Button>
                    </Stack>
                </Modal>
            )}
        </PageLayout>
    );
};

export default Signin;
